import React, { useState, useMemo, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewListIcon from '@mui/icons-material/ViewList';
import TableChartIcon from '@mui/icons-material/ViewKanban';
import FeaturesTable from './FeaturesTable';
import ResultsList from './ResultsList';
import ColumnSettingsDialog from './ColumnSettingsDialog';
import { getPreferredColumns, getAvailableData } from '../features_utils';




const EnrichedResultsColumn2 = ({hits, isLoading=false}) => {
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [viewMode, setViewMode] = useState('list');

  const { features, scores, ranking } = useMemo(
    () => getAvailableData(hits),
    [hits]
  );
  
  const allColumns = useMemo(() => {
    const cols = [...features, ...scores];
    return ranking ? ['_ranking_score', ...cols] : cols;
  }, [features, scores, ranking]);

  // Initialize visible columns
  useEffect(() => {
    if (allColumns.length === 0) {
      setSortBy(null);
      setVisibleColumns([]);
    } else {
      setSortBy(allColumns[0]);
      setVisibleColumns(getPreferredColumns(features));
    }
  }, [allColumns, features]);

  // Sort data based on selected sort field
  const sortedData = useMemo(() => {
    if (!hits) {
      return [];
    }
    if (!sortBy) {
      return hits;
    }
    const sorted = [...hits].sort((a, b) => {
      const aVal = sortBy === '_ranking_score' ? a._ranking_score : (a._features?.[sortBy] ?? a._scores?.[sortBy]);
      const bVal = sortBy === '_ranking_score' ? b._ranking_score : (b._features?.[sortBy] ?? b._scores?.[sortBy]);
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      let comparison = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [hits, sortBy, sortOrder]);


  const handleColumnToggle = (column) => {
    setVisibleColumns((prev) => {
      if (prev.includes(column)) {
        return prev.filter((col) => col !== column);
      } else {
        return [...prev, column];
      }
    });
  };

  const handleColumnsChange = (newColumns) => {
    setVisibleColumns(newColumns);
  };

  const handleSortChange = (column, order) => {
    setSortBy(column);
    setSortOrder(order);
  };

  const renderContent = () => {
    
    if (viewMode === 'list') {
      return <ResultsList hits={sortedData} />;
    }
  
    if (viewMode === 'grid') {
      return <FeaturesTable data={sortedData} 
              visibleColumns={visibleColumns} 
              visibleScores={scores}
              availableRanking={ranking}
              sortBy={sortBy} 
              sortOrder={sortOrder} 
              onSortChange={handleSortChange} 
      />;
    }

    return null;
    
  };

  const render = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!sortedData || sortedData.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
           Result list is still empty
        </Typography>
      );
    }

    return (
      <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 300 }}
          options={allColumns}
          value={sortBy || null}
          onChange={(_, v) => setSortBy(v)}
          getOptionLabel={(opt) => (opt === '_ranking_score' ? 'Final Ranking' : opt)}
          renderInput={(params) => (
            <TextField {...params} label="Sort by" />
          )}
        />

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="sort-order-label">Order</InputLabel>
          <Select
            labelId="sort-order-label"
            value={sortOrder}
            label="Order"
            onChange={(e) => setSortOrder(e.target.value)}
            size="small"
          >
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="list">
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="grid" >
              <TableChartIcon />
            </ToggleButton>
          </ToggleButtonGroup>

          <IconButton
            size="small"
            onClick={() => setSettingsOpen(true)}
            title="Column settings"
            disabled={viewMode !== 'grid'}
            sx={{ visibility: viewMode === 'grid' ? 'visible' : 'hidden' }}
          >
            <SettingsIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Settings Dialog */}
      <ColumnSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        allColumns={features}
        visibleColumns={visibleColumns}
        onColumnToggle={handleColumnToggle}
        onColumnsChange={handleColumnsChange}
      />

      {/* Results Table or List */}
      <Box sx={{ mt: 2, flex: 1, overflow: 'auto' }}>
        {renderContent()}
      </Box>
      </>
    );
  }

  return (
    
    <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Results
      </Typography>
      {render()}
    </Paper>
  );
};

export default EnrichedResultsColumn2;

